import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Popover } from 'antd-v5';
import { RootState } from 'app/AppStore';
import { ReactComponent as ReturnIcon } from 'assets/icons/circle-arrows.svg';
import { ReactComponent as ReturnSuccessIcon } from 'assets/icons/return-success.svg';
import { ReactComponent as ReturnWarnIcon } from 'assets/icons/return-warn.svg';
import { buildExadisReturnListLineKey, getExadisReturnListItemByKey } from 'domains/order/Order.store';
import { AddExadisReturnListItemSelection } from 'domains/order/Order.types';
import ExadisReturnQuantityPopover from 'pages/MyOrdersPage/ExadisReturnList/ExadisReturnQuantityPopover';
import { Icon, IconType, WithTooltip } from 'UI';

interface AddToExadisReturnListButtonProps {
  item: AddExadisReturnListItemSelection;
  disabled?: boolean;
  'data-cy'?: string;
}

const getReturnStatusIcon = (returnedQuantity: number | undefined, maxQuantity: number): IconType => {
  if (returnedQuantity === undefined) {
    return ReturnIcon;
  }
  return returnedQuantity >= maxQuantity ? ReturnSuccessIcon : ReturnWarnIcon;
};

const AddToExadisReturnListButton = ({
  item,
  disabled,
  'data-cy': dataCy,
}: AddToExadisReturnListButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const returnListItem = useSelector((state: RootState) =>
    getExadisReturnListItemByKey(state, buildExadisReturnListLineKey(item)),
  );

  const statusIcon = getReturnStatusIcon(returnListItem?.requestedQuantity, item.sourceInvoicedQuantity);

  const handleOpenChange = (open: boolean) => {
    if (!disabled) {
      setIsOpen(open);
    }
  };

  return (
    <WithTooltip title={t('exadis_return_list.add_tooltip', 'Add to my return list')}>
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
        content={<ExadisReturnQuantityPopover item={item} onClose={() => setIsOpen(false)} />}
        trigger="click"
        placement="top"
        data-cy={dataCy ?? 'add-to-exadis-return-list-popover'}
      >
        <Icon
          IconComponent={statusIcon}
          size={23}
          disabled={disabled}
          color={'brand_black'}
          data-cy={dataCy ?? 'add-to-exadis-return-list-button'}
        />
      </Popover>
    </WithTooltip>
  );
};

export default AddToExadisReturnListButton;
